/**
 * HeroBanner — shared layered "gacha banner" component used across screens.
 *
 * Same layer stack everywhere (backdrop, vignette, rays, particles, glow,
 * sprite, overlay, content, expiry badge). Differs only by:
 *   - layout: 'vertical' (sprite top, content bottom) for narrow side panels,
 *             'horizontal' (sprite left, content right) for wide screens.
 *   - accentColor: drives every glow / border / button color via --event-accent.
 *   - content: title / subtitle / meta / rewards / cta — all optional.
 *
 * The class names are still `event-banner-*` for legacy reasons — they're
 * shared across all variants (event, campaign, daily, shop) and the variant
 * is tagged via .hero-variant-{variant} for any per-variant tweaks.
 *
 * The CTA button has the class `hero-cta-btn` so callers can attach their own
 * click handler after rendering. Element badges carry data-tooltip-element so
 * callers can wire weakness/resist tooltips via attachHeroBannerTooltips. This
 * module returns HTML only — wiring up actions is the caller's responsibility.
 */

import { getBgUrl } from '../../combat/spriteConfig';
import { formatElement } from './GameTerms';
import { getElementIconUrl } from './ItemIcon';
import { Tooltip } from './Tooltip';
import { buildElementTooltip } from './ElementTooltip';

export type HeroLayout = 'vertical' | 'horizontal';

export type HeroVariant = 'event' | 'campaign' | 'daily' | 'shop-event';

export type HeroCtaState = 'available' | 'in_progress' | 'completed';

export interface HeroRewardConfig {
  type: 'skin' | 'unique' | 'featured';
  iconUrl: string;
  typeLabel: string;
  name: string;
}

export interface HeroElementsConfig {
  /** Defensive type ids visible on the card (typically capped at 3). */
  visible: string[];
  /** When true, append an "and more..." chip — late encounters / forks bring
   *  in additional types not shown here. */
  partial: boolean;
}

export interface HeroBannerConfig {
  variant: HeroVariant;
  /** Layout direction. Defaults to 'vertical'. */
  layout?: HeroLayout;
  /** Hex accent color (e.g. '#3a86ff') — themes the entire banner. */
  accentColor: string;
  /** Optional bg image key (loaded from /sprites/processed/bg/{key}.webp). */
  bgKey?: string;
  /** Optional hero sprite URL (already resolved). */
  spriteUrl?: string;
  spriteName?: string;
  /** When set, replaces the default single-sprite img inside the hero art
   *  area with arbitrary HTML. Use this to render multiple sprites (e.g. the
   *  shop hero shows base unit + skin variant side-by-side overlapped). */
  customArtHTML?: string;
  /** Small label above the title (e.g. "★ LIMITED EVENT ★"). */
  prebannerLabel: string;
  /** Smaller accent label below the title (e.g. event bundle name). */
  bundleLabel?: string;
  /** Big metallic-gradient title text. */
  title: string;
  /** Italic subtitle below the title. Use sparingly — bundle subtitles often
   *  read like cheesy taglines and crowd the layout. */
  subtitle?: string;
  /** Single-line meta text (e.g. "4 Rooms · Lv.17+", or "Waveweaver · Thalassan"). */
  meta?: string;
  /** Element badge row — rendered inline with the meta line so the chapter's
   *  defensive types read as part of the same "what am I walking into" glance.
   *  Hidden if empty. */
  elements?: HeroElementsConfig;
  /** Reward chips (skin / unique / featured strand). Hidden if empty. */
  rewards?: HeroRewardConfig[];
  /** Optional pre-built HTML block injected between meta and CTA — used by the
   *  shop variant to inject a strand info card / ability blurb. */
  infoBlocksHTML?: string;
  /** Optional pre-built expiry pill HTML, floats top-right. */
  expiryHTML?: string;
  /** Big CTA label (e.g. "Enter", "Recruit"). Ignored if hideCta is true. */
  ctaLabel: string;
  /** Optional sub-label rendered to the LEFT of the CTA button (e.g. "105g"). */
  ctaPriceLabel?: string;
  ctaState: HeroCtaState;
  /** When true, the helper omits the action row entirely. Used by callers
   *  (e.g. shop) that render their own multi-purchase rows inside infoBlocksHTML. */
  hideCta?: boolean;
}

export function buildHeroBanner(config: HeroBannerConfig): string {
  const layout = config.layout ?? 'vertical';
  const layoutClass = `is-${layout}`;
  const variantClass = `hero-variant-${config.variant}`;

  const backdropImage = config.bgKey ? getBgUrl(config.bgKey) : '';

  const spriteHTML = config.customArtHTML
    ?? (config.spriteUrl
      ? `<img src="${config.spriteUrl}" alt="${config.spriteName ?? ''}" class="event-hero-sprite">`
      : '');

  // Meta row — rooms/level meta and the chapter's defensive-type badges share
  // a single line. Each badge gets data-tooltip-element so callers can wire
  // hover/long-press weakness/resist tooltips via attachHeroBannerTooltips.
  const metaRowHTML = (() => {
    const els = config.elements;
    const hasMeta = !!config.meta;
    const hasElements = !!els && els.visible.length > 0;
    if (!hasMeta && !hasElements) return '';

    const parts: string[] = [];
    if (hasMeta) {
      parts.push(`<span class="event-banner-rooms">${config.meta}</span>`);
    }
    if (hasElements) {
      const badges = els!.visible.map(id => {
        const iconUrl = getElementIconUrl(id);
        const label = formatElement(id);
        return `<span class="event-banner-element" data-tooltip-element="${id}" data-element="${id}">`
          + `<img class="event-banner-element-icon" src="${iconUrl}" alt="${label}" width="18" height="18" draggable="false">`
          + `</span>`;
      }).join('');
      const more = els!.partial
        ? '<span class="event-banner-element event-banner-element--more">…more</span>'
        : '';
      parts.push(`<span class="event-banner-elements">${badges}${more}</span>`);
    }
    const sep = (hasMeta && hasElements)
      ? '<span class="event-banner-meta-sep" aria-hidden="true">·</span>'
      : '';
    return `<div class="event-banner-meta-row">${parts[0]}${sep}${parts.slice(1).join('')}</div>`;
  })();

  const rewardChipsHTML = (config.rewards ?? []).map(r => {
    const iconHTML = r.iconUrl
      ? `<img class="event-reward-icon" src="${r.iconUrl}" alt="">`
      : '';
    return `
      <div class="event-reward-chip ${r.type}">
        ${iconHTML}
        <div class="event-reward-info">
          <span class="event-reward-type">${r.typeLabel}</span>
          <span class="event-reward-name">${r.name}</span>
        </div>
      </div>
    `;
  }).join('');

  let actionHTML = '';
  if (config.hideCta) {
    actionHTML = '';
  } else if (config.ctaState === 'completed') {
    actionHTML = '<span class="event-banner-completed">&#10003; Completed</span>';
  } else {
    const priceHTML = config.ctaPriceLabel
      ? `<span class="event-banner-price">${config.ctaPriceLabel}</span>`
      : '';
    actionHTML = `
      ${priceHTML}
      <button class="event-banner-btn hero-cta-btn" type="button">${config.ctaLabel}</button>
    `;
  }

  return `
    <div class="event-banner event-hero ${layoutClass} ${variantClass}" style="--event-accent:${config.accentColor}">
      ${backdropImage ? `<div class="event-hero-backdrop" style="background-image:url('${backdropImage}')"></div>` : ''}
      <div class="event-hero-vignette"></div>
      <div class="event-hero-rays"></div>
      <div class="event-hero-particles"></div>
      <div class="event-hero-glow"></div>
      <div class="event-hero-art">${spriteHTML}</div>
      <div class="event-hero-overlay"></div>
      ${config.expiryHTML ? `<div class="event-hero-expiry-slot">${config.expiryHTML}</div>` : ''}
      <div class="event-banner-content event-hero-content">
        <div class="event-banner-prebanner">
          <span class="event-banner-prebanner-line"></span>
          <span class="event-banner-prebanner-label">${config.prebannerLabel}</span>
          <span class="event-banner-prebanner-line"></span>
        </div>
        <h2 class="event-banner-title">${config.title}</h2>
        ${config.bundleLabel ? `<span class="event-banner-bundle">${config.bundleLabel}</span>` : ''}
        ${config.subtitle ? `<span class="event-banner-subtitle">${config.subtitle}</span>` : ''}
        ${metaRowHTML}
        ${config.infoBlocksHTML ?? ''}
        ${rewardChipsHTML ? `<div class="event-banner-rewards">${rewardChipsHTML}</div>` : ''}
        ${actionHTML ? `<div class="event-banner-action">${actionHTML}</div>` : ''}
      </div>
    </div>
  `;
}

/**
 * Wire weakness/resist tooltips onto element badges inside a rendered hero
 * banner. Call after `slot.innerHTML = buildHeroBanner(...)`. Idempotent —
 * Tooltip.attach replaces any prior handler on the same element.
 */
export function attachHeroBannerTooltips(rootEl: HTMLElement | null | undefined): void {
  if (!rootEl) return;
  const badges = rootEl.querySelectorAll<HTMLElement>('.event-banner-element[data-tooltip-element]');
  badges.forEach(badge => {
    const id = badge.dataset.tooltipElement;
    if (!id) return;
    Tooltip.attach(badge, () => buildElementTooltip(id));
  });
}
